/**
 * MCP tool for device code OAuth login.
 *
 * Allows AI coding tools to trigger authentication without
 * requiring the user to run `canary login` in a separate terminal.
 *
 * @module
 */

import { ENV_URLS, envToProfile, resolveAppUrl, saveAuth } from '../auth';
import { openUrl, fetchOrgs, switchOrg } from '../login';
import type { StartResponse, PollResponse } from '../login';
import { resolveApiUrl } from './api-url';
import { toolJson } from './tool-helpers';

export function getLoginToolDefinitions() {
  return [
    {
      name: 'canary_login',
      description:
        'Authenticate with Canary using the device code OAuth flow. Opens a browser window for the user to approve. ' +
        'May take 30-120 seconds while waiting for the user to complete login. ' +
        'Returns the verification URL and user code in case the browser does not open automatically.',
      inputSchema: {
        type: 'object',
        properties: {
          env: {
            type: 'string',
            enum: ['prod', 'dev', 'local'],
            description: 'Target environment (default: prod)',
          },
          org: {
            type: 'string',
            description:
              'Organization name or ID to select if the user belongs to multiple orgs. If omitted, uses the default org.',
          },
        },
      },
    },
  ];
}

export async function handleLoginTool(
  tool: string,
  args: Record<string, unknown>
) {
  if (tool !== 'canary_login') return null;

  try {
    const env = args.env as string | undefined;
    const orgFlag = args.org as string | undefined;
    const envUrls = env ? ENV_URLS[env] : undefined;

    if (env && !envUrls) {
      return toolJson({ ok: false, error: `Unknown environment: ${env}. Valid: prod, dev, local` });
    }

    const apiUrl = envUrls?.api ?? resolveApiUrl();
    const appUrl = envUrls?.app ?? resolveAppUrl(apiUrl);

    // Step 1: Start device code flow
    const startRes = await fetch(`${apiUrl}/cli-login/start`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ appUrl }),
    });

    const startJson = (await startRes.json()) as StartResponse;
    if (!startRes.ok || !startJson.ok || !startJson.deviceCode || !startJson.userCode) {
      return toolJson({
        ok: false,
        error: `Login start failed: ${startJson.error ?? startRes.statusText}`,
      });
    }

    // Step 2: Open browser (non-fatal if it fails)
    if (startJson.verificationUrl) {
      try {
        openUrl(startJson.verificationUrl);
      } catch {
        // Browser open failed — user will need to open the URL manually
      }
    }

    // Step 3: Poll until approved/rejected/expired
    const intervalMs = (startJson.intervalSeconds ?? 3) * 1000;
    const expiresAt = startJson.expiresAt ? new Date(startJson.expiresAt).getTime() : null;

    let token: string | undefined;
    let initialOrgId: string | undefined;

    while (true) {
      if (expiresAt && Date.now() > expiresAt) {
        return toolJson({
          ok: false,
          error: 'Login code expired. Please try again.',
          userCode: startJson.userCode,
          verificationUrl: startJson.verificationUrl,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));

      const pollRes = await fetch(`${apiUrl}/cli-login/poll`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ deviceCode: startJson.deviceCode }),
      });

      const pollJson = (await pollRes.json()) as PollResponse;
      if (!pollRes.ok || !pollJson.ok) {
        return toolJson({
          ok: false,
          error: `Login poll failed: ${pollJson.error ?? pollRes.statusText}`,
        });
      }

      if (pollJson.status === 'approved' && pollJson.accessToken) {
        token = pollJson.accessToken;
        initialOrgId = pollJson.orgId;
        break;
      }

      if (pollJson.status === 'rejected') {
        return toolJson({ ok: false, error: 'Login rejected by user.' });
      }

      if (pollJson.status === 'expired') {
        return toolJson({ ok: false, error: 'Login expired. Please try again.' });
      }
    }

    // Step 4: Handle org selection
    const orgsData = await fetchOrgs(apiUrl, token);
    const orgs = orgsData?.organizations ?? [];

    let finalToken = token;
    let finalOrgId = initialOrgId;
    let finalOrgName: string | undefined;

    if (orgs.length <= 1) {
      finalOrgName = orgs[0]?.name;
    } else if (orgFlag) {
      // Match by name (case-insensitive) or ID
      const selectedOrg = orgs.find(
        (o) => o.name.toLowerCase() === orgFlag.toLowerCase() || o.id === orgFlag
      );
      if (!selectedOrg) {
        // Save the default token so they're at least logged in
        const profileName = env ? envToProfile(env) : undefined;
        await saveAuth({ token: finalToken, apiUrl, orgId: finalOrgId, orgName: orgs.find((o) => o.id === initialOrgId)?.name }, profileName);

        return toolJson({
          ok: false,
          error: `Organization "${orgFlag}" not found.`,
          loggedIn: true,
          currentOrg: orgs.find((o) => o.id === initialOrgId)?.name,
          availableOrgs: orgs.map((o) => ({ id: o.id, name: o.name, role: o.role })),
        });
      }

      if (selectedOrg.id !== initialOrgId) {
        const switchRes = await switchOrg(apiUrl, token, selectedOrg.id);
        if (!switchRes.ok || !switchRes.accessToken) {
          return toolJson({
            ok: false,
            error: `Failed to switch organization: ${switchRes.error ?? 'Unknown error'}`,
          });
        }
        finalToken = switchRes.accessToken;
        finalOrgId = switchRes.orgId;
        finalOrgName = switchRes.orgName;
      } else {
        finalOrgName = selectedOrg.name;
      }
    } else {
      // Multiple orgs, no flag — use default
      const defaultOrg = orgs.find((o) => o.id === initialOrgId);
      finalOrgName = defaultOrg?.name;
    }

    // Step 5: Save auth
    const profileName = env ? envToProfile(env) : undefined;
    await saveAuth({ token: finalToken, apiUrl, orgId: finalOrgId, orgName: finalOrgName }, profileName);

    return toolJson({
      ok: true,
      message: 'Login successful. Token saved.',
      orgName: finalOrgName,
      orgId: finalOrgId,
      ...(orgs.length > 1
        ? { availableOrgs: orgs.map((o) => ({ id: o.id, name: o.name, role: o.role })) }
        : {}),
    });
  } catch (err) {
    return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
  }
}
